/**
 * One refusal, one toast — across the two independent channels a venue reports
 * it on.
 *
 * Every venue that refuses a new order publishes BOTH a synthetic REJECTED
 * `Order` on the account stream (Hyperliquid's command-loop error arm,
 * exchange-toss's `reject`) and an `OrderExecutionError` notification. They
 * arrive on different subjects, through different frontend paths, and carry no
 * shared id — only the venue's message text, which both stamp verbatim from the
 * same string.
 *
 * So the message text is the dedupe key. The order frame is the richer report
 * (it has the symbol and quantity), so it wins:
 *
 * - order frame first  → it registers the message here, and the notification
 *   that follows is skipped.
 * - notification first → it toasts under `executionErrorToastId(message)`, and
 *   the order frame dismisses exactly that toast before showing its own.
 *
 * Either ordering ends on one visible toast carrying the most detail available.
 * Nothing is dropped from notification history, which dedupes separately.
 */

/**
 * How long a surfaced rejection suppresses the matching notification. Long
 * enough to cover the gap between two publishes on the same venue tick, short
 * enough that genuinely re-attempting the same order later still reports.
 */
export const REJECT_DEDUPE_WINDOW_MS = 15_000

const surfaced = new Map<string, number>()

/** The sonner id an `OrderExecutionError` toast is shown under. */
export function executionErrorToastId(message: string | undefined): string {
  return `order-execution-${message ?? ''}`
}

/**
 * Record that an order frame has already told the user about this refusal.
 * No-ops on an empty message: with no text there is nothing to match on, and
 * keying every reasonless rejection under `''` would suppress unrelated ones.
 */
export function markRejectionSurfaced(message: string | undefined, now = Date.now()): void {
  if (!message) return
  prune(now)
  surfaced.set(message, now)
}

/** Whether an order frame already reported this exact refusal, recently. */
export function wasRejectionSurfaced(message: string | undefined, now = Date.now()): boolean {
  if (!message) return false
  const at = surfaced.get(message)
  return at !== undefined && now - at <= REJECT_DEDUPE_WINDOW_MS
}

/**
 * Drop expired entries. Called on write rather than on a timer so the map
 * cannot grow without bound across a long session — a venue in a reject loop
 * republishes the same few strings, but a stream of distinct ones (per-symbol
 * reasons) would otherwise accumulate for the life of the tab.
 */
function prune(now: number): void {
  for (const [message, at] of surfaced) {
    if (now - at > REJECT_DEDUPE_WINDOW_MS) surfaced.delete(message)
  }
}

/** Test seam: forget everything surfaced so far. */
export function resetRejectDedupe(): void {
  surfaced.clear()
}
